import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { api } from "../api";
import { scheduleAndroidTimeline } from "../notifications";
import { syncAndroidSchedules } from "../nativeSchedule";
import { formatDate, messageDisplayText, readableError, toIso, toLocalInput } from "../composables/useFormat";
import { useToast } from "../composables/useToast";
import { useAuthStore } from "./auth";
import { useChannelStore } from "./channels";
import { loadOfflineSnapshot, saveOfflineSnapshot } from "../offlineSnapshot";
import type { Attachment, Message, MessageDetail, Project, ProjectDetail, Reminder, Schedule, SearchPayload, TodayPayload, Todo } from "../types";

type ChannelCache = {
  messages: Message[];
  attachments: Attachment[];
  todos: Todo[];
  reminders: Reminder[];
  schedules: Schedule[];
  projects: Project[];
  lastSyncAt?: string;
};

export const useDataStore = defineStore("data", () => {
  const toast = useToast();
  const authStore = useAuthStore();
  const channelStore = useChannelStore();
  const messages = ref<Message[]>([]);
  const attachments = ref<Attachment[]>([]);
  const todos = ref<Todo[]>([]);
  const reminders = ref<Reminder[]>([]);
  const schedules = ref<Schedule[]>([]);
  const projects = ref<Project[]>([]);
  const todayData = ref<TodayPayload | null>(null);
  const reviewMode = ref<"daily" | "weekly">("daily");
  const reviewData = ref<Record<string, any> | null>(null);
  const searchResults = ref<SearchPayload | null>(null);
  const lastSearchQuery = ref("");
  const activeProject = ref<ProjectDetail | null>(null);
  const privacyInfo = ref<{ principles: string[]; ai: Record<string, unknown> } | null>(null);
  const syncStatus = ref("未同步");
  const lastSyncAt = ref("");
  const lastSyncCount = ref(0);
  const lastSyncError = ref("");
  const loading = ref(false);
  const detail = ref<MessageDetail | null>(null);
  const detailEdit = ref({ organizing_note: "" });
  const detailTodoDueAt = ref("");
  const detailTodoPriority = ref<Todo["priority"]>("normal");
  const detailReminderAt = ref("");
  const detailScheduleStartAt = ref("");
  const detailScheduleEndAt = ref("");
  const detailScheduleAlarmEnabled = ref(false);
  const detailProjectId = ref("");
  const onlyCurrentChannel = ref(false);
  const uploadedAttachments = ref<Attachment[]>([]);
  const channelCache = ref<Record<string, ChannelCache>>({});
  const offlineReadOnly = ref(false);
  const needsSync = ref(false);

  const pendingMessages = computed(() => messages.value.filter((item) =>
    item.author_id === authStore.user?.id && (item.status === "pending_confirmation" || item.ai_needs_confirmation)));
  const pendingCount = computed(() => pendingMessages.value.length);
  const todayReminders = computed(() => [...(todayData.value?.overdue_reminders || []), ...(todayData.value?.reminders_today || [])]);
  const todaySchedules = computed(() => todayData.value?.schedules_today || []);
  const reviewTopics = computed(() => (reviewData.value?.topics || []) as string[]);
  const reviewMessages = computed(() => (reviewData.value?.messages || []) as Message[]);
  const reviewPending = computed(() => (reviewData.value?.pending_confirmations || []) as Message[]);
  const searchResultCount = computed(() => {
    if (!searchResults.value) return 0;
    return searchResults.value.messages.length + searchResults.value.todos.length + searchResults.value.reminders.length + searchResults.value.schedules.length;
  });

  async function refreshAll(showStatus = true) {
    loading.value = true;
    try {
      if (channelStore.channels.length === 0) await channelStore.loadChannels();
      const current = channelStore.currentChannel;
      const cached = current ? channelCache.value[current.id] : null;
      const snapshot = await api.sync(current ? { channels: [current.id], since: cached?.lastSyncAt } : {});
      channelStore.channels = snapshot.channels;
      if (current) {
        messages.value = activeRows(cached?.lastSyncAt ? mergeById(cached.messages, snapshot.messages, "created_at") : snapshot.messages);
        attachments.value = activeRows(cached?.lastSyncAt ? mergeById(cached.attachments, snapshot.attachments || [], "updated_at") : (snapshot.attachments || []));
        todos.value = activeRows(cached?.lastSyncAt ? mergeById(cached.todos, snapshot.todos, "updated_at") : snapshot.todos);
        reminders.value = activeRows(cached?.lastSyncAt ? mergeById(cached.reminders, snapshot.reminders, "updated_at") : snapshot.reminders);
        schedules.value = activeRows(cached?.lastSyncAt ? mergeById(cached.schedules, snapshot.schedules || [], "updated_at") : (snapshot.schedules || []));
        projects.value = activeRows(cached?.lastSyncAt ? mergeById(cached.projects, snapshot.projects, "updated_at") : snapshot.projects);
        channelCache.value[current.id] = {
          messages: messages.value,
          attachments: attachments.value,
          todos: todos.value,
          reminders: reminders.value,
          schedules: schedules.value,
          projects: projects.value,
          lastSyncAt: snapshot.server_time
        };
      } else {
        messages.value = activeRows(snapshot.messages);
        attachments.value = activeRows(snapshot.attachments || []);
        todos.value = activeRows(snapshot.todos);
        reminders.value = activeRows(snapshot.reminders);
        schedules.value = activeRows(snapshot.schedules || []);
        projects.value = activeRows(snapshot.projects);
      }
      syncStatus.value = `已同步 ${formatDate(snapshot.server_time)}`;
      offlineReadOnly.value = false;
      needsSync.value = false;
      lastSyncAt.value = snapshot.server_time;
      lastSyncCount.value = snapshot.messages.length + snapshot.todos.length + snapshot.reminders.length
        + (snapshot.schedules?.length || 0) + snapshot.projects.length + (snapshot.attachments?.length || 0);
      lastSyncError.value = "";
      scheduleAndroidTimeline(reminders.value, schedules.value, channelStore.mutedChannelIds).catch(() => undefined);
      syncAndroidSchedules(schedules.value).then(applyNativeScheduleUpdates).catch(() => undefined);
      if (current && authStore.user) {
        saveOfflineSnapshot({ userId: authStore.user.id, channelId: current.id, savedAt: Date.now(), channels: channelStore.channels, messages: messages.value, attachments: attachments.value, todos: todos.value, reminders: reminders.value, schedules: schedules.value, projects: projects.value, lastSyncAt: snapshot.server_time }).catch(() => undefined);
      }
    } catch (error) {
      lastSyncError.value = error instanceof Error ? error.message : String(error);
      if (showStatus) syncStatus.value = `同步失败：${lastSyncError.value}`;
      throw error;
    } finally {
      loading.value = false;
    }
  }

  function clearLocal() {
    messages.value = [];
    attachments.value = [];
    todos.value = [];
    reminders.value = [];
    schedules.value = [];
    projects.value = [];
    todayData.value = null;
    reviewData.value = null;
    searchResults.value = null;
    lastSearchQuery.value = "";
    activeProject.value = null;
    detail.value = null;
    uploadedAttachments.value = [];
    channelCache.value = {};
    syncStatus.value = "未同步";
    lastSyncAt.value = "";
    lastSyncCount.value = 0;
    lastSyncError.value = "";
    offlineReadOnly.value = false;
    needsSync.value = false;
  }

  async function restoreOfflineSnapshot(channelId?: string) {
    const userId = authStore.user?.id;
    const id = channelId || channelStore.currentChannel?.id;
    if (!userId || !id) return false;
    const snapshot = await loadOfflineSnapshot(userId, id).catch(() => null);
    if (!snapshot) return false;
    channelStore.channels = snapshot.channels;
    messages.value = snapshot.messages;
    attachments.value = snapshot.attachments;
    todos.value = snapshot.todos;
    reminders.value = snapshot.reminders;
    schedules.value = snapshot.schedules;
    projects.value = snapshot.projects;
    channelCache.value[id] = { messages: snapshot.messages, attachments: snapshot.attachments, todos: snapshot.todos, reminders: snapshot.reminders, schedules: snapshot.schedules, projects: snapshot.projects, lastSyncAt: snapshot.lastSyncAt };
    lastSyncAt.value = snapshot.lastSyncAt || "";
    offlineReadOnly.value = true;
    syncStatus.value = "离线只读：显示最近同步快照";
    return true;
  }

  function applyChannelCache(channelId: string) {
    const cached = channelCache.value[channelId];
    if (!cached) return false;
    messages.value = cached.messages;
    attachments.value = cached.attachments;
    todos.value = cached.todos;
    reminders.value = cached.reminders;
    schedules.value = cached.schedules;
    projects.value = cached.projects;
    activeProject.value = null;
    detail.value = null;
    return true;
  }

  async function sendMessage(content: string) {
    if (offlineReadOnly.value) throw new Error("离线只读状态下不能新建记录");
    try {
      const created = await api.createMessage(content, false, channelStore.currentChannel?.id, uploadedAttachments.value.map((item) => item.id));
      uploadedAttachments.value = [];
      needsSync.value = true;
      await refreshAll(false);
      return created;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function undoMessage(id: string) {
    try {
      await api.deleteMessage(id);
      await refreshAll(false);
      await loadToday();
      toast.success("已撤销发送");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function openMessage(id: string) {
    detail.value = await api.messageDetail(id);
    const recentKey = `mindback.recentViews.${authStore.user?.id || "anonymous"}`;
    const recentIds = (() => {
      try {
        return JSON.parse(localStorage.getItem(recentKey) || "[]") as string[];
      } catch {
        return [];
      }
    })();
    localStorage.setItem(recentKey, JSON.stringify([id, ...recentIds.filter((item) => item !== id)].slice(0, 20)));
    detailEdit.value = {
      organizing_note: detail.value.message.organizing_note || ""
    };
    detailTodoDueAt.value = "";
    detailTodoPriority.value = "normal";
    detailReminderAt.value = "";
    detailScheduleStartAt.value = toLocalInput(detail.value.message.ai_reminder_at);
    detailScheduleEndAt.value = "";
    detailScheduleAlarmEnabled.value = false;
    detailProjectId.value = "";
  }

  function closeMessage() {
    detail.value = null;
  }

  async function saveDetail() {
    if (!detail.value) return;
    try {
      await api.patchMessage(detail.value.message.id, detailEdit.value as Partial<Message>);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      toast.success("整理备注已保存");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function detailAction(action: string) {
    if (!detail.value) return;
    const payload: { action: string; title?: string; due_at?: string; priority?: Todo["priority"]; remind_at?: string; end_at?: string; all_day?: boolean; remind_minutes_before?: number; alarm_enabled?: boolean } = {
      action,
      title: messageDisplayText(detail.value.message, detail.value.attachments)
    };
    if (action === "todo") {
      if (detailTodoDueAt.value) payload.due_at = toIso(detailTodoDueAt.value);
      payload.priority = detailTodoPriority.value;
    }
    if (action === "reminder") {
      if (!detailReminderAt.value) {
        toast.error("请先选择提醒时间。");
        throw new Error("请先选择提醒时间。");
      }
      payload.remind_at = toIso(detailReminderAt.value);
    }
    if (action === "schedule") {
      if (!detailScheduleStartAt.value) {
        toast.error("请先选择日程开始时间。");
        throw new Error("请先选择日程开始时间。");
      }
      payload.remind_at = toIso(detailScheduleStartAt.value);
      if (detailScheduleEndAt.value) payload.end_at = toIso(detailScheduleEndAt.value);
      payload.remind_minutes_before = 15;
      payload.alarm_enabled = detailScheduleAlarmEnabled.value;
    }
    try {
      await api.confirmMessage(detail.value.message.id, payload);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      await loadToday();
      toast.success("处理完成");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function attachDetailProject() {
    if (!detail.value || !detailProjectId.value) return;
    try {
      await api.addProjectMessage(detailProjectId.value, detail.value.message.id);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      toast.success("已关联项目");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function removeProjectMessage(projectId: string) {
    if (!detail.value) return;
    try {
      await api.removeProjectMessage(projectId, detail.value.message.id);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      if (activeProject.value?.project.id === projectId) await openProject(projectId);
      toast.success("已移除项目关联");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteAi() {
    if (!detail.value) return;
    try {
      await api.deleteAi(detail.value.message.id);
      await openMessage(detail.value.message.id);
      await refreshAll(false);
      toast.success("AI 派生结果已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function retryAi() {
    if (!detail.value) return;
    await api.retryAi(detail.value.message.id);
    await openMessage(detail.value.message.id);
    await refreshAll(false);
    toast.success("已重新加入自动整理队列");
  }

  async function deleteCurrentMessage() {
    if (!detail.value) return;
    try {
      await api.deleteMessage(detail.value.message.id);
      detail.value = null;
      await refreshAll(false);
      await loadToday();
      toast.success("原始消息已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function quickConfirm(message: Message, action: "todo" | "archive" | "ignore") {
    try {
      await api.confirmMessage(message.id, { action, title: message.ai_summary || messageDisplayText(message, attachments.value) });
      await refreshAll(false);
      await loadToday();
      toast.success("待确认项已处理");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function loadToday() {
    todayData.value = await api.today(onlyCurrentChannel.value ? channelStore.currentChannel?.id : undefined);
  }

  async function patchTodo(id: string, payload: Partial<Todo>) {
    try {
      await api.patchTodo(id, payload);
      await refreshAll(false);
      await loadToday();
      toast.success("待办已更新");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function createTodo(title: string, dueAt?: string, priority: Todo["priority"] = "normal") {
    try {
      await api.createTodo({ title, due_at: dueAt ? toIso(dueAt) : undefined, priority });
      await refreshAll(false);
      await loadToday();
      toast.success("待办已创建");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function patchReminder(id: string, payload: Partial<Reminder> & { snooze_minutes?: number }) {
    try {
      await api.patchReminder(id, payload);
      await refreshAll(false);
      await loadToday();
      toast.success("提醒已更新");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteReminder(id: string) {
    try {
      await api.deleteReminder(id);
      await refreshAll(false);
      await loadToday();
      toast.success("提醒已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function createManualReminder(title: string, remindAt: string) {
    try {
      await api.createReminder({ title, remind_at: toIso(remindAt), kind: "manual" });
      await refreshAll(false);
      await loadToday();
      toast.success("提醒已创建");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function createSchedule(payload: {
    title: string;
    start_at: string;
    end_at?: string;
    all_day?: boolean;
    remind_minutes_before?: number | null;
    alarm_enabled?: boolean;
    message_id?: string;
    channel_id?: string;
  }) {
    try {
      const response = await api.createSchedule(payload);
      await syncOneSchedule(response.schedule);
      await refreshAll(false);
      await loadToday();
      toast.success("日程已创建");
      return response.schedule;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function patchSchedule(id: string, payload: Partial<Schedule>) {
    try {
      const response = await api.patchSchedule(id, payload);
      await syncOneSchedule(response.schedule);
      await refreshAll(false);
      await loadToday();
      toast.success("日程已更新");
      return response.schedule;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteSchedule(id: string) {
    const existing = schedules.value.find((item) => item.id === id);
    try {
      await api.deleteSchedule(id);
      if (existing) await syncAndroidSchedules([{ ...existing, status: "deleted" }]).catch(() => []);
      await refreshAll(false);
      await loadToday();
      toast.success("日程已删除");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function loadReview(mode: "daily" | "weekly") {
    reviewMode.value = mode;
    const channel = onlyCurrentChannel.value ? channelStore.currentChannel?.id : undefined;
    reviewData.value = mode === "daily" ? await api.dailyReview(undefined, channel) : await api.weeklyReview(channel);
  }

  async function runSearch(query: string, filters: {
    channel?: string;
    from_date?: string;
    to_date?: string;
    types?: string[];
    attachment_type?: string;
    project_id?: string;
  } = {}) {
    lastSearchQuery.value = query;
    try {
      searchResults.value = await api.search({
        q: query,
        channel: filters.channel ?? (onlyCurrentChannel.value ? channelStore.currentChannel?.id : undefined),
        ...filters
      });
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function reloadCurrentView(routeName: string) {
    if (routeName === "today") {
      await loadToday();
    } else if (routeName === "review") {
      await loadReview(reviewMode.value);
    } else if (routeName === "memory" && searchResults.value && lastSearchQuery.value) {
      await runSearch(lastSearchQuery.value);
    }
  }

  async function createProject(name: string) {
    try {
      const created = await api.createProject(name, "", channelStore.currentChannel?.id);
      await refreshAll(false);
      await openProject(created.project.id);
      toast.success("项目已创建");
      return created.project;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function openProject(id: string) {
    activeProject.value = await api.project(id);
  }

  async function patchProject(id: string, payload: Partial<Project>) {
    try {
      const response = await api.patchProject(id, payload);
      await refreshAll(false);
      await openProject(response.project.id);
      toast.success("项目已保存");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function deleteProject(id: string) {
    try {
      await api.deleteProject(id);
      activeProject.value = null;
      await refreshAll(false);
      toast.success("项目已删除，原始消息保留");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function loadPrivacy() {
    privacyInfo.value = await api.privacy();
  }

  async function downloadExport(format: "json" | "md" | "csv") {
    try {
      const data = await api.exportData(format);
      const text = typeof data === "string" ? data : JSON.stringify(data, null, 2);
      const blob = new Blob([text], { type: format === "json" ? "application/json" : "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `mindback-export.${format}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("导出已开始");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function clearAccountData() {
    try {
      await api.deleteAccountData();
      await refreshAll(false);
      await loadToday();
      toast.success("账号数据已清空");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function uploadFiles(
    files: FileList | File[],
    onProgress?: (completed: number, total: number) => void
  ) {
    const list = Array.from(files);
    const uploaded: Array<{ attachment: Attachment; file: File }> = [];
    const failed: Array<{ file: File; error: string }> = [];
    for (const [index, file] of list.entries()) {
      try {
        const response = await api.upload(file);
        uploadedAttachments.value.push(response.attachment);
        uploaded.push({ attachment: response.attachment, file });
      } catch (error) {
        const message = readableError(error);
        failed.push({ file, error: message });
        toast.error(`${file.name}：${message}`);
      } finally {
        onProgress?.(index + 1, list.length);
      }
    }
    return { uploaded, failed };
  }

  function removeUploadedAttachment(id: string) {
    uploadedAttachments.value = uploadedAttachments.value.filter((item) => item.id !== id);
  }

  function restoreUploadedAttachments(ids: string[]) {
    uploadedAttachments.value = ids
      .map((id) => attachments.value.find((item) => item.id === id))
      .filter((item): item is Attachment => Boolean(item));
  }

  async function downloadAttachment(attachment: Attachment) {
    try {
      const blob = await api.attachmentBlob(attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  function mergeById<T extends { id: string; updated_at?: string; created_at?: string }>(existing: T[], incoming: T[], sortField: "updated_at" | "created_at") {
    const merged = new Map(existing.map((item) => [item.id, item]));
    incoming.forEach((item) => merged.set(item.id, item));
    return [...merged.values()].sort((a, b) => String(b[sortField] || "").localeCompare(String(a[sortField] || "")));
  }

  function activeRows<T extends { status?: string }>(rows: T[]) {
    return rows.filter((item) => item.status !== "deleted");
  }

  async function syncOneSchedule(schedule: Schedule) {
    const updates = await syncAndroidSchedules([schedule]).catch(() => []);
    await applyNativeScheduleUpdates(updates);
  }

  async function applyNativeScheduleUpdates(updates: Array<{ id: string; android_calendar_event_id?: string }>) {
    let changed = false;
    for (const update of updates) {
      if (!update.android_calendar_event_id) continue;
      const current = schedules.value.find((item) => item.id === update.id);
      if (current?.android_calendar_event_id === update.android_calendar_event_id) continue;
      const response = await api.patchSchedule(update.id, { android_calendar_event_id: update.android_calendar_event_id } as Partial<Schedule>);
      schedules.value = mergeById(schedules.value, [response.schedule], "updated_at");
      changed = true;
    }
    const currentChannel = channelStore.currentChannel;
    if (changed && currentChannel && channelCache.value[currentChannel.id]) {
      channelCache.value[currentChannel.id] = {
        ...channelCache.value[currentChannel.id],
        attachments: attachments.value,
        schedules: schedules.value
      };
    }
  }

  return {
    messages,
    attachments,
    todos,
    reminders,
    schedules,
    projects,
    todayData,
    reviewMode,
    reviewData,
    searchResults,
    activeProject,
    privacyInfo,
    syncStatus,
    lastSyncAt,
    lastSyncCount,
    lastSyncError,
    loading,
    offlineReadOnly,
    needsSync,
    detail,
    detailEdit,
    detailTodoDueAt,
    detailTodoPriority,
    detailReminderAt,
    detailScheduleStartAt,
    detailScheduleEndAt,
    detailScheduleAlarmEnabled,
    detailProjectId,
    onlyCurrentChannel,
    uploadedAttachments,
    pendingMessages,
    pendingCount,
    todayReminders,
    todaySchedules,
    reviewTopics,
    reviewMessages,
    reviewPending,
    searchResultCount,
    refreshAll,
    clearLocal,
    restoreOfflineSnapshot,
    applyChannelCache,
    sendMessage,
    undoMessage,
    openMessage,
    closeMessage,
    saveDetail,
    detailAction,
    attachDetailProject,
    removeProjectMessage,
    deleteAi,
    retryAi,
    deleteCurrentMessage,
    quickConfirm,
    loadToday,
    patchTodo,
    createTodo,
    patchReminder,
    deleteReminder,
    createManualReminder,
    createSchedule,
    patchSchedule,
    deleteSchedule,
    loadReview,
    runSearch,
    reloadCurrentView,
    createProject,
    openProject,
    patchProject,
    deleteProject,
    loadPrivacy,
    downloadExport,
    clearAccountData,
    uploadFiles,
    removeUploadedAttachment,
    restoreUploadedAttachments,
    downloadAttachment
  };
});
